import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiRequest, serializeModel } from "../../../api";

import {
  CompanyBody,
  CompanyOverview as ApiCompanyOverview,
  CompanySiteOverview as ApiSiteOverview,
  Interactions,
  Reports,
  SiteItem,
  TimeRange,
} from "@vviinn/user-managment-client-ts";

import { getFixedHistogramWidth } from "../../../helpers/time/fixedHistogramWidth";
import { FetchableState } from "..";
import { fetchSuccinctSites } from "../sites/sites-slice";
import { getCompany } from "../company";
import { changeActiveSite } from "../site/site-slice";

type CompanyOverview = FetchableState & {
  apiRequests: number;
  productsPerSite: number;
  companyId: CompanyBody["id"] | null;
  timeRange: TimeRange | null;
  updatedAt: null | number;
};

export type CompanySiteOverview = FetchableState & {
  interactions: Interactions | null;
  reports: Reports | null;
  siteId: SiteItem["id"] | null;
  companyId: CompanyBody["id"] | null;
  timeRange: TimeRange | null;
  updatedAt: null | number;
};

const initialCompanyOverview: CompanyOverview = {
  loading: false,
  error: false,
  apiRequests: 0,
  productsPerSite: 0,
  status: "idle",
  companyId: null,
  timeRange: null,
  updatedAt: null,
};

const initialCompanySiteOverview: CompanySiteOverview = {
  loading: false,
  error: false,
  reports: null,
  interactions: null,
  status: "idle",
  siteId: null,
  companyId: null,
  timeRange: null,
  updatedAt: null,
};

type CompanyOverviewRequestParams = {
  companyId: CompanyBody["id"];
  timeRange: TimeRange;
};

type CompanySiteOverviewRequestParams = {
  companyId: CompanyBody["id"];
  siteId: SiteItem["id"];
  timeRange: TimeRange;
};

export const fetchCompanyOverviewData = createAsyncThunk(
  "dashboard/fetchCompanyOverview",
  async ({ api, value }: ApiRequest<CompanyOverviewRequestParams>) => {
    const { companyId, timeRange } = value;
    if (!companyId) return;

    const response = await api.getCompanyOverview(companyId, timeRange);
    return {
      ...serializeModel<ApiCompanyOverview>(response.value),
      timeRange,
    };
  },
);

export const fetchCompanySiteOverviewData = createAsyncThunk(
  "dashboard/fetchCompanySiteOverview",
  async ({ api, value }: ApiRequest<CompanySiteOverviewRequestParams>) => {
    const { companyId, siteId, timeRange } = value;
    if (!companyId) return;

    const response = await api.getCompanySiteOverview(
      companyId,
      siteId,
      timeRange,
      undefined,
      undefined,
      getFixedHistogramWidth(timeRange),
    );
    return {
      ...serializeModel<ApiSiteOverview>(response.value),
      siteId,
      companyId,
      timeRange,
    };
  },
);

type DashboardState = {
  overview: Pick<ApiCompanyOverview, "totalApiRequests">;
  selectedTimeRange: TimeRange;
  companyOverview: CompanyOverview;
  companySiteOverview: CompanySiteOverview;
  selectedCompanyId: CompanyBody["id"] | null;
  selectedSiteId: SiteItem["id"] | null;
};

const initialState: DashboardState = {
  selectedTimeRange: "month",
  companyOverview: initialCompanyOverview,
  companySiteOverview: initialCompanySiteOverview,
  selectedCompanyId: null,
  selectedSiteId: null,
  overview: {
    totalApiRequests: 0,
  },
};

// Overview fetches can resolve out of order (e.g. rapid time-period switching firing several
// requests before the earlier ones finish) — without this, a late-arriving response for a
// since-superseded selection would overwrite the data for what's actually selected now.
const isCurrentCompanyRequest = (
  state: DashboardState,
  params: { timeRange: TimeRange },
) => params.timeRange === state.selectedTimeRange;

const isCurrentSiteRequest = (
  state: DashboardState,
  params: { siteId: SiteItem["id"]; timeRange: TimeRange },
) =>
  params.siteId === state.selectedSiteId &&
  params.timeRange === state.selectedTimeRange;

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    changeTimeRange(state, { payload }: PayloadAction<TimeRange>) {
      state.selectedTimeRange = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCompanyOverviewData.pending, (state) => {
      state.companyOverview.loading = true;
      state.companyOverview.error = false;
    });
    builder.addCase(fetchCompanyOverviewData.rejected, (state, action) => {
      if (!isCurrentCompanyRequest(state, action.meta.arg.value)) return;
      state.companyOverview = initialCompanyOverview;
    });
    builder.addCase(
      fetchCompanyOverviewData.fulfilled,
      (state, { payload }) => {
        if (!payload) return;
        if (
          payload.id !== state.selectedCompanyId ||
          !isCurrentCompanyRequest(state, payload)
        )
          return;

        state.companyOverview.loading = false;
        state.companyOverview.error = false;
        state.companyOverview.companyId = payload.id;
        state.companyOverview.timeRange = payload.timeRange;
        state.companyOverview.apiRequests = payload.totalApiRequests;
        state.companyOverview.updatedAt = new Date().getTime();
        const { totalApiRequests } = payload;
        state.overview = { totalApiRequests };
      },
    );

    builder.addCase(fetchCompanySiteOverviewData.pending, (state) => {
      state.companySiteOverview.loading = true;
      state.companySiteOverview.error = false;
    });
    builder.addCase(fetchCompanySiteOverviewData.rejected, (state, action) => {
      if (!isCurrentSiteRequest(state, action.meta.arg.value)) return;
      state.companySiteOverview.loading = false;
      state.companySiteOverview.error = true;
      state.companySiteOverview = initialCompanySiteOverview;
    });
    builder.addCase(
      fetchCompanySiteOverviewData.fulfilled,
      (state, { payload }) => {
        if (!payload) return;
        if (!isCurrentSiteRequest(state, payload)) return;

        state.companySiteOverview.loading = false;
        state.companySiteOverview.error = false;
        state.companySiteOverview.siteId = payload.siteId;
        state.companySiteOverview.companyId = payload.companyId;
        state.companySiteOverview.timeRange = payload.timeRange;
        state.companySiteOverview.updatedAt = new Date().getTime();
        state.companySiteOverview.reports = payload.reports;
        state.companySiteOverview.interactions = payload.interactions;
      },
    );

    builder.addCase(fetchSuccinctSites.fulfilled, (state, { payload }) => {
      if (payload.length === 0) {
        state.companySiteOverview = initialCompanySiteOverview;
      }
    });
    builder.addCase(fetchSuccinctSites.rejected, (state) => {
      state.companySiteOverview = initialCompanySiteOverview;
    });

    builder.addCase(getCompany.fulfilled, (state, { payload }) => {
      if (!payload) {
        state.companyOverview = initialCompanyOverview;
      } else {
        state.selectedCompanyId = payload.id;
      }
    });
    builder.addCase(getCompany.rejected, (state) => {
      state.companyOverview = initialCompanyOverview;
    });
    builder.addCase(changeActiveSite, (state, { payload }) => {
      state.selectedSiteId = payload.id;
    });
  },
});

export const { changeTimeRange } = dashboardSlice.actions;
export default dashboardSlice.reducer;
